<template>
  <div class="mini-tab-content">
    <div class="interc-toolbar">
      <span class="interc-toolbar__title">
        <feather-icon
          icon="FlagIcon"
          size="13"
          class="mr-25 text-success"
        />
        {{ outcomeCountDisplay }}
      </span>
      <button
        v-if="!activeOutcome && $hasAbility('regulacao.gerenciar')"
        class="btn btn-sm btn-success"
        @click="openOutcomeModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="13"
          class="mr-25"
        />
        {{ $t('involvedPanel.outcome.toolbar.registerButton') }}
      </button>
    </div>

    <div
      v-if="!involvedOutcomes.length"
      class="interc-empty"
    >
      <feather-icon
        icon="FlagIcon"
        size="26"
      />
      <p>{{ $t('involvedPanel.outcome.empty.noOutcomes') }}</p>
    </div>

    <div
      v-if="involvedOutcomes.length"
      class="interc-list"
    >
      <div
        v-for="oc in involvedOutcomes"
        :key="oc.id"
        :class="['outcome-item', { 'outcome-item--cancelled': oc.cancelled_at }]"
      >
        <div class="interc-item__top">
          <div class="outcome-item__badge">
            <feather-icon
              :icon="oc.cancelled_at ? 'XCircleIcon' : 'CheckCircleIcon'"
              size="11"
              class="mr-25"
            />
            {{ oc.outcome ? oc.outcome.name : '—' }}
          </div>
          <button
            v-if="!oc.cancelled_at && $hasAbility('regulacao.gerenciar')"
            class="interc-item__del"
            :title="$t('involvedPanel.outcome.list.cancelButtonTooltip')"
            @click="cancelOutcome(oc.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="13"
            />
          </button>
        </div>
        <p
          v-if="oc.observation"
          class="interc-item__desc"
        >
          {{ oc.observation }}
        </p>
        <div class="interc-item__meta">
          <feather-icon
            icon="UserIcon"
            size="11"
          />
          <span>{{ oc.registered_by ? oc.registered_by.name : '—' }}</span>
          <span class="interc-item__dot" />
          <feather-icon
            icon="ClockIcon"
            size="11"
          />
          <span>{{ oc.created_at | formatDate }}</span>
        </div>
        <div
          v-if="oc.cancelled_at"
          class="interc-item__cancelled-row"
        >
          <feather-icon
            icon="XCircleIcon"
            size="11"
            class="mr-25"
          />
          {{ $t('involvedPanel.outcome.list.cancelledByLabel') }}
          <strong>{{ oc.cancelled_by ? oc.cancelled_by.name : '—' }}</strong>
          · {{ oc.cancelled_at | formatDate }}
        </div>
      </div>
    </div>

    <b-modal
      v-model="showOutcomeModal"
      :title="$t('involvedPanel.outcome.modal.title')"
      size="md"
      centered
      no-close-on-backdrop
      @hidden="newOutcome = { outcome_id: null, observation: '' }"
    >
      <div
        v-if="loadingOutcomesCatalog"
        class="interc-loading"
      >
        <b-spinner
          small
          variant="success"
        />
        <span>{{ $t('involvedPanel.outcome.modal.loading') }}</span>
      </div>
      <template v-else>
        <div class="form-group mb-2">
          <label class="interc-label"><span class="required">{{ $t('involvedPanel.outcome.modal.requiredMark') }}</span> {{ $t('involvedPanel.outcome.modal.outcomeLabel') }}</label>
          <v-select
            v-model="newOutcome.outcome_id"
            :options="outcomesCatalog"
            :reduce="(o) => o.id"
            label="name"
            :placeholder="$t('involvedPanel.outcome.modal.outcomePlaceholder')"
            class="vselect"
          />
        </div>
        <div class="form-group mb-0">
          <label class="interc-label">{{ $t('involvedPanel.outcome.modal.observationLabel') }}</label>
          <textarea
            v-model="newOutcome.observation"
            class="form-control"
            rows="3"
            :placeholder="$t('involvedPanel.outcome.modal.observationPlaceholder')"
            maxlength="2000"
          />
        </div>
      </template>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showOutcomeModal = false"
        >
          {{ $t('involvedPanel.outcome.modal.closeButton') }}
        </button>
        <button
          class="btn btn-sm btn-success"
          :disabled="savingOutcome || !newOutcome.outcome_id"
          @click="saveOutcome"
        >
          <b-spinner
            v-if="savingOutcome"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="CheckCircleIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.outcome.modal.registerButton') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'OutcomePanel',
  mixins: [involvedPanelMixin],
  props: {
    initialOutcomes: { type: Array, default: () => [] },
  },
  data() {
    return {
      involvedOutcomes: [...(this.initialOutcomes || [])],
      outcomesCatalog: [],
      loadingOutcomesCatalog: false,
      savingOutcome: false,
      showOutcomeModal: false,
      newOutcome: { outcome_id: null, observation: '' },
    }
  },
  computed: {
    activeOutcome() {
      return this.involvedOutcomes.find(o => !o.cancelled_at) || null
    },
    outcomeCountDisplay() {
      if (!this.involvedOutcomes.length) {
        return this.$t('involvedPanel.outcome.toolbar.noOutcomes')
      }
      const count = this.involvedOutcomes.length
      const unit = count === 1 ? this.$t('involvedPanel.outcome.toolbar.outcomeCountUnit') : this.$t('involvedPanel.outcome.toolbar.outcomeCountUnitPlural')
      return `${count} ${unit}`
    },
  },
  methods: {
    openOutcomeModal() {
      this.showOutcomeModal = true
      this.loadOutcomesCatalog()
    },
    loadOutcomesCatalog() {
      this.loadingOutcomesCatalog = true
      let url = `${this.$store.state.api}outcomes/for-context?context=Envolvido`
      if (this.disciplineId) url += `&discipline_id=${this.disciplineId}`
      this.$http
        .get(url)
        .then(r => { this.outcomesCatalog = r.data.data || [] })
        .catch(() => { this.$message(this.$t('involvedPanel.outcome.messages.error.title'), this.$t('involvedPanel.outcome.messages.error.catalogLoadError'), 'error') })
        .finally(() => { this.loadingOutcomesCatalog = false })
    },
    async saveOutcome() {
      this.savingOutcome = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_involved_outcomes`, {
          occurrence_involved_id: this.occurrenceInvolvedId,
          outcome_id: this.newOutcome.outcome_id,
          observation: this.newOutcome.observation || null,
        })
        const saved = res.data.data || res.data
        this.involvedOutcomes.push(saved)
        this.newOutcome = { outcome_id: null, observation: '' }
        this.showOutcomeModal = false
        this.$emit('outcome-saved')
        this.$message(this.$t('involvedPanel.outcome.messages.success.title'), this.$t('involvedPanel.outcome.messages.success.saved'), 'success')
      } catch (e) {
        this.$message(this.$t('involvedPanel.outcome.messages.error.title'), this.$t('involvedPanel.outcome.messages.error.saveError'), 'error')
      } finally {
        this.savingOutcome = false
      }
    },
    async cancelOutcome(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.outcome.messages.confirm.cancelMessage'),
        {
          title: this.$t('involvedPanel.outcome.messages.confirm.cancelTitle'), okTitle: this.$t('involvedPanel.outcome.messages.confirm.cancelOk'), cancelTitle: this.$t('involvedPanel.outcome.messages.confirm.cancelCancel'), okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_involved_outcomes/${id}`)
        const updated = res.data.data || res.data
        const idx = this.involvedOutcomes.findIndex(o => o.id === id)
        if (idx !== -1) this.$set(this.involvedOutcomes, idx, updated)
      } catch (e) {
        this.$message(this.$t('involvedPanel.outcome.messages.error.title'), this.$t('involvedPanel.outcome.messages.error.cancelError'), 'error')
      }
    },
  },
}
</script>
